import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  FileDownload
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import AppLayout from '../../components/layout/AppLayout';
import DataTable from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ConfirmScadenzaDialog from '../../components/scadenze/ConfirmScadenzaDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import scadenzeService from '../../services/scadenzeService';
import tipologieService from '../../services/tipologieService';
import { getDaysUntilExpiry, getDaysDisplayText, getDaysTextColor } from '../../utils/dateUtils';
import { formatReminderDisplay } from '../../utils/reminderUtils';
import { StatoScadenza, type Scadenza } from '../../types/scadenza.types';
import type { TableColumn } from '../../types/common.types';
import { getErrorMessage } from '../../utils/api';

const ScadenzeListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null
  });
  const [bulkActionDialog, setBulkActionDialog] = useState<{
    open: boolean;
    action: 'complete' | 'cancel' | 'delete' | null;
  }>({
    open: false,
    action: null
  });
  const [confirmScadenzaDialog, setConfirmScadenzaDialog] = useState<{
    open: boolean;
    scadenza: Scadenza | null;
  }>({
    open: false,
    scadenza: null
  });

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('dataScadenza');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Filters
  const [filters, setFilters] = useState<{
    stato: string;
    tipologiaId: string;
    search: string;
  }>({
    stato: searchParams.get('stato') || '',
    tipologiaId: '',
    search: ''
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch tipologie for filter
  const { data: tipologie } = useQuery({
    queryKey: ['tipologie-active'],
    queryFn: () => tipologieService.getAllActive()
  });

  // Fetch scadenze
  const { data, isLoading } = useQuery({
    queryKey: ['scadenze', page, rowsPerPage, filters.stato, filters.tipologiaId, debouncedSearch, sortBy, sortOrder],
    queryFn: () => {
      // Backend expects 'sort' parameter with '-' prefix for descending
      const sort = sortOrder === 'desc' ? `-${sortBy}` : sortBy;

      return scadenzeService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        stato: filters.stato ? (filters.stato as StatoScadenza) : undefined,
        tipologiaId: filters.tipologiaId || undefined,
        sort
      });
    }
  });

  // Helper per invalidare tutte le query dashboard dopo operazioni sulle scadenze
  const invalidateAllScadenzeQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['scadenze'] });
    queryClient.invalidateQueries({ queryKey: ['scadenze-stats'] });
    queryClient.invalidateQueries({ queryKey: ['scadenze-mese-corrente'] });
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => scadenzeService.delete(id),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      setDeleteDialog({ open: false, id: null });
      enqueueSnackbar('Scadenza eliminata con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione'), { variant: 'error' });
    }
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { numeroFattura: string; noteRinnovo?: string; nuovaDataScadenza?: string } }) => scadenzeService.complete(id, data),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      setConfirmScadenzaDialog({ open: false, scadenza: null });
      enqueueSnackbar('Scadenza completata con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il completamento'), { variant: 'error' });
    }
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: (id: string) => scadenzeService.cancel(id),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      enqueueSnackbar('Scadenza annullata con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'annullamento'), { variant: 'error' });
    }
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: () => scadenzeService.exportToCsv({
      search: debouncedSearch,
      stato: filters.stato as StatoScadenza | undefined,
      tipologiaId: filters.tipologiaId || undefined
    }),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scadenze_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  });

  // Bulk complete mutation
  const bulkCompleteMutation = useMutation({
    mutationFn: (ids: string[]) => scadenzeService.bulkComplete(ids),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      setSelectedRows([]);
      setBulkActionDialog({ open: false, action: null });
      enqueueSnackbar('Scadenze completate con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il completamento'), { variant: 'error' });
    }
  });

  // Bulk cancel mutation
  const bulkCancelMutation = useMutation({
    mutationFn: (ids: string[]) => scadenzeService.bulkCancel(ids),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      setSelectedRows([]);
      setBulkActionDialog({ open: false, action: null });
      enqueueSnackbar('Scadenze annullate con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'annullamento'), { variant: 'error' });
    }
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => scadenzeService.bulkDelete(ids),
    onSuccess: () => {
      invalidateAllScadenzeQueries();
      setSelectedRows([]);
      setBulkActionDialog({ open: false, action: null });
      enqueueSnackbar('Scadenze eliminate con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione'), { variant: 'error' });
    }
  });


  const columns: TableColumn<Scadenza>[] = [
    {
      id: 'cliente',
      label: 'Cliente',
      minWidth: 200,
      mobilePriority: 1,
      format: (_, row) =>
        row.cliente ? `${row.cliente.cognome} ${row.cliente.nome}`.trim() : '-'
    },
    {
      id: 'tipologia',
      label: 'Tipologia',
      minWidth: 150,
      mobilePriority: 1,
      format: (_, row) => row.tipologia?.nome || '-'
    },
    {
      id: 'descrizione',
      label: 'Descrizione',
      minWidth: 200,
      mobilePriority: 2,
      format: (value) => {
        const descrizione = value as string | undefined;
        if (!descrizione) return '-';
        // Tronca la descrizione se troppo lunga
        return descrizione.length > 50 ? `${descrizione.substring(0, 50)}...` : descrizione;
      }
    },
    {
      id: 'dataScadenza',
      label: 'Data Scadenza',
      minWidth: 130,
      mobilePriority: 1,
      format: (value, row) => {
        if (!value) return '-';
        const strValue = value as string;
        const date = new Date(strValue);
        const daysUntil = getDaysUntilExpiry(strValue);
        const isExpired = daysUntil < 0;
        
        return (
          <Typography 
            variant="body2"
            sx={{ 
              color: isExpired ? 'error.main' : 'text.primary',
              fontWeight: isExpired ? 600 : 400
            }}
          >
            {`${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`}
          </Typography>
        );
      }
    },
    {
      id: 'giorniRimanenti',
      label: 'Giorni Rimanenti',
      minWidth: 150,
      mobilePriority: 1,
      format: (_, row) => {
        const daysUntil = getDaysUntilExpiry(row.dataScadenza);
        return (
          <Chip
            size="small"
            label={getDaysDisplayText(daysUntil)}
            sx={{
              backgroundColor: daysUntil < 0 ? 'error.main' : 
                               daysUntil <= 3 ? 'warning.main' : 
                               daysUntil <= 7 ? 'warning.main' : 'success.main',
              color: 'white',
              fontWeight: daysUntil < 0 ? 600 : 500,
              '& .MuiChip-label': {
                color: 'white'
              }
            }}
          />
        );
      }
    },
    {
      id: 'stato',
      label: 'Stato',
      minWidth: 150,
      mobilePriority: 1,
      format: (_, row) => <StatusChip stato={row.stato} />
    },
    {
      id: 'giorniPreavviso',
      label: 'Giorni Preavviso',
      minWidth: 120,
      align: 'center',
      mobilePriority: 2,
      format: (value) => {
        if (!value) return '-';
        return formatReminderDisplay(value as number | number[]);
      }
    },
    {
      id: 'assegnatoA',
      label: 'Assegnato A',
      minWidth: 150,
      mobilePriority: 2,
      format: (_, row) =>
        row.assegnatoA ? `${row.assegnatoA.nome} ${row.assegnatoA.cognome}` : '-'
    },
    {
      id: 'numeroFattura',
      label: 'Numero Fattura',
      minWidth: 140,
      mobilePriority: 2,
      format: (_, row) => row.numeroFattura || '-'
    }
  ];

  const handleDelete = (id: string) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = () => {
    if (deleteDialog.id) {
      deleteMutation.mutate(deleteDialog.id);
    }
  };

  const handleComplete = (scadenza: Scadenza) => {
    setConfirmScadenzaDialog({ open: true, scadenza });
  };

  const handleConfirmComplete = (data: { numeroFattura: string; noteRinnovo?: string; nuovaDataScadenza?: string }) => {
    if (confirmScadenzaDialog.scadenza) {
      completeMutation.mutate({ id: confirmScadenzaDialog.scadenza.id, data });
    }
  };

  const handleCancel = (id: string) => {
    cancelMutation.mutate(id);
  };

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(direction);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleExport = () => {
    exportMutation.mutate();
  };

  const handleBulkAction = (action: 'complete' | 'cancel' | 'delete') => {
    setBulkActionDialog({ open: true, action });
  };

  const confirmBulkAction = () => {
    if (!bulkActionDialog.action || selectedRows.length === 0) return;

    switch (bulkActionDialog.action) {
      case 'complete':
        bulkCompleteMutation.mutate(selectedRows);
        break;
      case 'cancel':
        bulkCancelMutation.mutate(selectedRows);
        break;
      case 'delete':
        bulkDeleteMutation.mutate(selectedRows);
        break;
    }
  };

  const getBulkActionDialogContent = () => {
    const count = selectedRows.length;
    switch (bulkActionDialog.action) {
      case 'complete':
        return {
          title: 'Conferma Completamento Multiplo',
          message: `Sei sicuro di voler completare ${count} scadenze selezionate?`,
          confirmText: 'Completa',
          confirmColor: 'success' as const
        };
      case 'cancel':
        return {
          title: 'Conferma Annullamento Multiplo',
          message: `Sei sicuro di voler annullare ${count} scadenze selezionate?`,
          confirmText: 'Annulla Scadenze',
          confirmColor: 'warning' as const
        };
      case 'delete':
        return {
          title: 'Conferma Eliminazione Multipla',
          message: `Sei sicuro di voler eliminare ${count} scadenze selezionate? Questa azione non può essere annullata.`,
          confirmText: 'Elimina',
          confirmColor: 'error' as const
        };
      default:
        return {
          title: '',
          message: '',
          confirmText: '',
          confirmColor: 'primary' as const
        };
    }
  };

  const renderActions = (row: Scadenza) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="Visualizza">
        <IconButton size="small" onClick={() => navigate(`/scadenze/${row.id}`)}>
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.stato !== StatoScadenza.COMPLETATA && row.stato !== StatoScadenza.ANNULLATA && (
        <>
          <Tooltip title="Modifica">
            <IconButton size="small" onClick={() => navigate(`/scadenze/${row.id}/edit`)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Completa">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleComplete(row)}
              disabled={completeMutation.isPending}
            >
              <CheckCircle fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Annulla">
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleCancel(row.id)}
              disabled={cancelMutation.isPending}
            >
              <Cancel fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="Elimina">
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDelete(row.id)}
          disabled={deleteMutation.isPending}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (isLoading && !data) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento scadenze..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Gestione Scadenze
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': {
              whiteSpace: 'nowrap'
            }
          }}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              Esporta CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/scadenze/new')}
            >
              Nuova Scadenza
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtri
          </Typography>
          <form onSubmit={(e) => e.preventDefault()}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)'
              },
              gap: { xs: 2, md: 3 }
            }}>
              <TextField
                label="Cerca"
                placeholder="Cliente, tipologia..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                fullWidth
                helperText={filters.search !== debouncedSearch ? "Ricerca in corso..." : undefined}
              />
              <TextField
                select
                label="Stato"
                value={filters.stato}
                onChange={(e) => handleFilterChange('stato', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value={StatoScadenza.ATTIVA}>Attiva</MenuItem>
                <MenuItem value={StatoScadenza.IN_SCADENZA}>In Scadenza</MenuItem>
                <MenuItem value={StatoScadenza.SCADUTA}>Scaduta</MenuItem>
                <MenuItem value={StatoScadenza.COMPLETATA}>Completata</MenuItem>
                <MenuItem value={StatoScadenza.ANNULLATA}>Annullata</MenuItem>
              </TextField>
              <TextField
                select
                label="Tipologia"
                value={filters.tipologiaId}
                onChange={(e) => handleFilterChange('tipologiaId', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">Tutte</MenuItem>
                {tipologie?.map((tip) => (
                  <MenuItem key={tip.id} value={tip.id}>
                    {tip.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </form>
        </Paper>

        {selectedRows.length > 0 && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                {selectedRows.length} scadenze selezionate
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => handleBulkAction('complete')}
                  disabled={bulkCompleteMutation.isPending}
                >
                  Completa Selezionate
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={() => handleBulkAction('cancel')}
                  disabled={bulkCancelMutation.isPending}
                >
                  Annulla Selezionate
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => handleBulkAction('delete')}
                  disabled={bulkDeleteMutation.isPending}
                >
                  Elimina Selezionate
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        <DataTable
          columns={columns}
          data={data?.data || []}
          total={data?.total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
          sortField={sortBy}
          sortDirection={sortOrder}
          onSort={handleSort}
          selectable
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          actions={renderActions}
          loading={isLoading}
          getRowSx={(row) => {
            const daysUntil = getDaysUntilExpiry(row.dataScadenza);
            if (daysUntil < 0) {
              return {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                borderLeft: '4px solid',
                borderLeftColor: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)'
                }
              };
            } else if (daysUntil === 0) {
              return {
                backgroundColor: 'rgba(211, 47, 47, 0.02)',
                borderLeft: '3px solid',
                borderLeftColor: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.05)'
                }
              };
            }
            return {};
          }}
          getCardSx={(row) => {
            const daysUntil = getDaysUntilExpiry(row.dataScadenza);
            if (daysUntil < 0) {
              return {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                borderLeft: '4px solid',
                borderLeftColor: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)'
                }
              };
            } else if (daysUntil === 0) {
              return {
                backgroundColor: 'rgba(211, 47, 47, 0.02)',
                borderLeft: '3px solid',
                borderLeftColor: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.05)'
                }
              };
            }
            return {};
          }}
          emptyStateComponent={
            <EmptyState
              title="Nessuna scadenza trovata"
              message={
                debouncedSearch || filters.stato || filters.tipologiaId
                  ? "Non ci sono scadenze che corrispondono ai filtri selezionati. Prova a modificare i criteri di ricerca."
                  : "Non hai ancora creato nessuna scadenza. Inizia creando la tua prima scadenza per tenere traccia delle scadenze dei tuoi clienti."
              }
              actionLabel={!debouncedSearch && !filters.stato && !filters.tipologiaId ? "Crea Nuova Scadenza" : undefined}
              onAction={!debouncedSearch && !filters.stato && !filters.tipologiaId ? () => navigate('/scadenze/new') : undefined}
            />
          }
        />

        <ConfirmScadenzaDialog
          open={confirmScadenzaDialog.open}
          scadenza={confirmScadenzaDialog.scadenza}
          onConfirm={handleConfirmComplete}
          onCancel={() => setConfirmScadenzaDialog({ open: false, scadenza: null })}
          loading={completeMutation.isPending}
        />

        <ConfirmDialog
          open={deleteDialog.open}
          title="Conferma Eliminazione"
          message="Sei sicuro di voler eliminare questa scadenza? Questa azione non puo essere annullata."
          confirmText="Elimina"
          confirmColor="error"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteDialog({ open: false, id: null })}
          loading={deleteMutation.isPending}
        />

        <ConfirmDialog
          open={bulkActionDialog.open}
          title={getBulkActionDialogContent().title}
          message={getBulkActionDialogContent().message}
          confirmText={getBulkActionDialogContent().confirmText}
          confirmColor={getBulkActionDialogContent().confirmColor}
          onConfirm={confirmBulkAction}
          onCancel={() => setBulkActionDialog({ open: false, action: null })}
          loading={
            bulkCompleteMutation.isPending ||
            bulkCancelMutation.isPending ||
            bulkDeleteMutation.isPending
          }
        />
      </Box>
    </AppLayout>
  );
};

export default ScadenzeListPage;
